import type { LucideIcon } from 'lucide-react';
import { CreditCard, LayoutGrid, School, User } from 'lucide-react';
import {
  PRODUCTS,
  PRODUCTS_LIST,
  filterNavItems,
  type ProductId,
} from '@/lib/constants/products';
import { COMPLIANCE_NAV_GROUPS } from '@/lib/constants/complianceNav';
import { scoreEntry } from '@/lib/search/match';
import { HUB_KEYWORDS, PATH_KEYWORDS } from '@/lib/search/destinationKeywords';
import type { SearchContext, SearchProvider, SearchResult } from '@/lib/search/types';

/** One reachable place in the app. */
export interface DestinationEntry {
  path: string;
  label: string;
  icon: LucideIcon;
  hub?: ProductId;
  /** Owning hub's display name, or 'Settings' — shown as the result's subtitle. */
  hubName: string;
  /** Sub-grouping within a hub, e.g. Compliance's "Binder". */
  section?: string;
  keywords: string[];
  /** Owned by a hub the user has not subscribed to. See SearchResult.locked. */
  locked?: boolean;
}

/** Below this, a match is coincidence rather than intent. */
const MIN_SCORE = 30;
const MAX_RESULTS = 12;
/** Locked hubs are an answer, not a catalogue — a few, never a wall. */
const MAX_LOCKED = 3;

/**
 * Every word a hub's pages would have matched on, flattened onto the hub.
 *
 * A locked hub is offered as a single result, so it has to answer for all of
 * them: "fire drill" must reach Compliance even though the Drill Log is not a
 * navItem, and "ratios" must reach Scheduling even though no page is named that.
 */
function hubVocabulary(productId: ProductId): string[] {
  const product = PRODUCTS[productId];
  const words = [
    ...(HUB_KEYWORDS[productId] ?? []),
    ...product.navItems.flatMap((item) => [
      item.label.toLowerCase(),
      ...(PATH_KEYWORDS[item.path] ?? []),
    ]),
  ];
  if (productId === 'compliance') {
    words.push(
      ...COMPLIANCE_NAV_GROUPS.flatMap((group) =>
        group.items.flatMap((item) => [item.label.toLowerCase(), ...(PATH_KEYWORDS[item.path] ?? [])]),
      ),
    );
  }
  return [...new Set(words)];
}

/**
 * Every destination this user can actually reach.
 *
 * Access is not re-derived here: hubs come from `hasProductAccess` and items
 * run through the same `filterNavItems` the sidebar uses. Search must never
 * surface a page the sidebar would hide, and the only way to guarantee that as
 * both evolve is to share the gate rather than copy it.
 */
export function buildDestinationIndex(ctx: SearchContext): DestinationEntry[] {
  const entries: DestinationEntry[] = [];

  // A hub's landing page and its own "Dashboard" nav item are the same route.
  // Dropping the later one as a duplicate would make "dashboard" find nothing,
  // so a collision folds the discarded label in as a searchable keyword instead.
  const byPath = new Map<string, DestinationEntry>();
  const push = (e: DestinationEntry) => {
    const existing = byPath.get(e.path);
    if (existing) {
      if (!existing.keywords.includes(e.label.toLowerCase())) {
        existing.keywords = [...existing.keywords, e.label.toLowerCase()];
      }
      return;
    }
    byPath.set(e.path, e);
    entries.push(e);
  };

  for (const product of PRODUCTS_LIST) {
    if (product.status === 'hidden') continue;

    if (!ctx.hasProductAccess(product.id) && !ctx.isPlatformAdmin) {
      // An owner searching for a hub they have not bought used to get silence,
      // while the grid immediately below offered that same hub as a "Subscribe"
      // tile. Worse, with the real answer missing, weak matches floated up and
      // read as the answer. Offer the hub itself instead — one entry, carrying
      // its pages' vocabulary so "fire drill" still lands on Compliance.
      if (ctx.isOwner && product.status === 'live') {
        push({
          path: product.route,
          label: product.name,
          icon: product.icon,
          hub: product.id,
          hubName: product.name,
          keywords: hubVocabulary(product.id),
          locked: true,
        });
      }
      continue;
    }

    // The hub itself — "finance" should find Finance Hub, not just its pages.
    push({
      path: product.route,
      label: product.name,
      icon: product.icon,
      hub: product.id,
      hubName: product.name,
      keywords: [...(HUB_KEYWORDS[product.id] ?? []), ...(PATH_KEYWORDS[product.route] ?? [])],
    });

    // Mirrors HubLayoutContent exactly, including the effectiveRole promotion.
    // The two expressions happen to agree today because filterNavItems checks
    // isAdmin before role, but copying the shape means a change there cannot
    // quietly widen what search reveals.
    const role = ctx.getProductRole(product.id);
    const effectiveRole = ctx.isAdmin ? (product.defaultRole ?? null) : role;
    const items = filterNavItems(
      [...product.navItems, ...(product.footerItems ?? [])],
      effectiveRole,
      product.defaultRole,
      ctx.isPlatformAdmin,
      ctx.isAdmin,
      ctx.multiSchool,
    );

    for (const item of items) {
      push({
        path: item.path,
        label: item.label,
        icon: item.icon,
        hub: product.id,
        hubName: product.name,
        section: item.section,
        keywords: PATH_KEYWORDS[item.path] ?? [],
      });
    }
  }

  // Compliance keeps a second nav source: its many pages are folded into a few
  // sidebar groups, so the Drill Log and Food Program never appear in navItems.
  // Those are exactly the pages people search for by name.
  if (ctx.hasProductAccess('compliance') || ctx.isPlatformAdmin) {
    const compliance = PRODUCTS.compliance;
    const role = ctx.getProductRole('compliance');
    // Every page in this hub is adminOnly, and these bypass the hub's navItems
    // entirely — so they have to be handed the same gate explicitly. Without it
    // a non-admin member with Compliance access would find the Drill Log and
    // Staff Files in search while their sidebar showed nothing at all.
    const gated = filterNavItems(
      COMPLIANCE_NAV_GROUPS.flatMap((group) =>
        group.items.map((item) => ({
          path: item.path,
          label: item.label,
          icon: group.icon,
          adminOnly: true,
          section: group.label === item.label ? undefined : group.label,
        })),
      ),
      ctx.isAdmin ? (compliance.defaultRole ?? null) : role,
      compliance.defaultRole,
      ctx.isPlatformAdmin,
      ctx.isAdmin,
      ctx.multiSchool,
    );

    for (const item of gated) {
      push({
        path: item.path,
        label: item.label,
        icon: item.icon,
        hub: 'compliance',
        hubName: compliance.name,
        section: item.section,
        keywords: PATH_KEYWORDS[item.path] ?? [],
      });
    }
  }

  // Cross-hub and account destinations. Gating mirrors AdminSidebar.
  push({
    path: '/calendar',
    label: 'Calendar',
    icon: LayoutGrid,
    hubName: 'Everywhere',
    keywords: PATH_KEYWORDS['/calendar'] ?? [],
  });
  push({
    path: '/settings',
    label: 'My Account',
    icon: User,
    hubName: 'Settings',
    keywords: PATH_KEYWORDS['/settings'] ?? [],
  });
  if (ctx.isAdmin || ctx.isPlatformAdmin) {
    push({
      path: '/settings/schools',
      label: 'Schools',
      icon: School,
      hubName: 'Settings',
      keywords: PATH_KEYWORDS['/settings/schools'] ?? [],
    });
  }
  if (ctx.isOwner || ctx.isPlatformAdmin) {
    push({
      path: '/settings/subscriptions',
      label: 'Subscription',
      icon: CreditCard,
      hubName: 'Settings',
      keywords: PATH_KEYWORDS['/settings/subscriptions'] ?? [],
    });
  }

  return entries;
}

/** Rank an already-built index against a query. Pure — the unit under test. */
export function searchDestinations(
  query: string,
  index: DestinationEntry[],
): SearchResult[] {
  if (!query.trim()) return [];

  const scored = index
    .map((entry) => ({
      entry,
      score: scoreEntry(query, {
        label: entry.label,
        keywords: entry.keywords,
        hubName: entry.hubName,
      }),
    }))
    .filter((r) => r.score >= MIN_SCORE)
    .sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label));

  // Somewhere you can go always beats somewhere you would have to buy, however
  // well the locked one matches.
  const owned = scored.filter((r) => !r.entry.locked);
  const locked = scored.filter((r) => r.entry.locked).slice(0, MAX_LOCKED);

  return [...owned, ...locked]
    .slice(0, MAX_RESULTS)
    .map(({ entry, score }): SearchResult => ({
      id: `dest:${entry.path}`,
      title: entry.label,
      subtitle: entry.section ? `${entry.hubName} · ${entry.section}` : entry.hubName,
      href: entry.path,
      icon: entry.icon,
      layer: 'destination',
      hub: entry.hub,
      locked: entry.locked,
      score,
    }));
}

export const destinationProvider: SearchProvider = {
  layer: 'destination',
  search: (query, ctx) => searchDestinations(query, buildDestinationIndex(ctx)),
};
